import { useState } from 'react';
import { Dialog, Button } from '@workspace/design-system/src/components';
import { getPlanLabel } from '../constants';

interface PlanConfirmDialogProps {
  mode: 'apply' | 'extend';
  channelName: string;
  planType: string;
  durationMonths?: string; // 연장 모드일 때 표시할 개월 수
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * 요금제 적용/연장 확인 다이얼로그
 * apply-plan-confirm-dialog, apply-plan-no-payment-confirm-dialog,
 * extend-plan-no-payment-confirm-dialog를 통합한 공통 컴포넌트
 */
export function PlanConfirmDialog({
  mode,
  channelName,
  planType,
  durationMonths,
  onClose,
  onConfirm
}: PlanConfirmDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const planLabel = getPlanLabel(planType);
  const isApplyMode = mode === 'apply';
  
  const title = isApplyMode
    ? `${planLabel} 요금제를 적용하시겠습니까?`
    : `${planLabel} 요금제를 연장하시겠습니까?`;
  
  const description = isApplyMode
    ? `'${channelName}' 채널에 ${planLabel} 요금제가 즉시 적용됩니다.`
    : durationMonths
      ? `기존 요금제 만료일로부터 ${durationMonths}개월간\n${channelName} 채널에 ${planLabel} 요금제가 연장됩니다.`
      : `${channelName} 채널에 ${planLabel} 요금제가 연장됩니다.`;
  
  const confirmButtonText = isApplyMode ? '적용하기' : '연장하기';

  return (
    <Dialog>
      <Dialog.Content className="max-w-[480px]">
        <Dialog.Header
          title={title}
          type="question"
          showIcon
        />

        <Dialog.Body>
          <p className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-pre-line">
            {description}
          </p>
        </Dialog.Body>

        <Dialog.Footer className="flex gap-3 items-center justify-end">
          <Button
            styleType="outline-basic"
            styleSize="M-40"
            onClick={onClose}
          >
            닫기
          </Button>
          <Button
            styleType="primary"
            styleSize="M-40"
            onClick={async () => {
              setIsLoading(true);
              try {
                await onConfirm();
              } catch (error) {
                console.error('Confirmation failed:', error);
                throw error;
              } finally {
                setIsLoading(false);
              }
            }}
            isLoading={isLoading}
          >
            {confirmButtonText}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

